
import { useEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import { Plus, Pencil, FileCode, AlertTriangle, X, Shield, GitBranch, KeyRound } from 'lucide-react'
import { useMcpConnections } from '../../hooks/useMcpConnections'
import { useEngineManager, useRegistry } from '../../hooks/useWorkspace'
import type { McpConnectionEntry } from '../../engine/mcp/types'
import { requiresConnectionRecreation } from '../../engine/mcp/connectionEditing'
import { useToast } from '../Toast'
import CreateMcpConnectionModal from './CreateMcpConnectionModal'
import EditMcpConnectionModal from './EditMcpConnectionModal'
import ViewMcpConfigModal from './ViewMcpConfigModal'
import McpOAuthStateInspector from './McpOAuthStateInspector'
import './McpSettings.css'
import './CreateMcpConnectionModal.css'

export default function McpConnectionsSettings() {
    const { connections, remove } = useMcpConnections()
    const engine = useEngineManager()
    const { entries } = useRegistry()
    const toast = useToast()
    const [isCreateOpen, setIsCreateOpen] = useState(false)
    const [editingConnection, setEditingConnection] = useState<McpConnectionEntry | null>(null)
    const [viewingConnection, setViewingConnection] = useState<McpConnectionEntry | null>(null)
    const [revokingId, setRevokingId] = useState<string | null>(null)
    const [pendingRevokeId, setPendingRevokeId] = useState<string | null>(null)
    const [isOAuthInspectorOpen, setIsOAuthInspectorOpen] = useState(false)

    const handleRevokeConfirm = async () => {
        if (!pendingRevokeId) return
        const configBlobId = pendingRevokeId
        setPendingRevokeId(null)

        try {
            setRevokingId(configBlobId)
            await remove(configBlobId)
        } catch (err) {
            toast('Failed to revoke connection: ' + (err as Error).message, { type: 'error' })
        } finally {
            setRevokingId(null)
        }
    }

    // Escape key closes the revoke confirmation modal
    useEffect(() => {
        if (!pendingRevokeId) return
        const onKey = (e: KeyboardEvent) => {
            if (e.key === 'Escape') setPendingRevokeId(null)
        }
        document.addEventListener('keydown', onKey)
        return () => document.removeEventListener('keydown', onKey)
    }, [pendingRevokeId])

    return (
        <div className="mcp-settings-container">
            <div className="mcp-settings-header">
                <span className="mcp-settings-title">MCP Connections</span>
                <div style={{ display: 'flex', gap: 8 }}>
                    <button className="mcp-btn mcp-config-btn" onClick={() => setIsOAuthInspectorOpen(true)} title="Inspect OAuth session state">
                        <Shield size={16} style={{ marginRight: 6 }} />
                        OAuth State
                    </button>
                    <button className="mcp-btn mcp-btn-primary" onClick={() => setIsCreateOpen(true)}>
                        <Plus size={16} style={{ marginRight: 6 }} />
                        New Connection
                    </button>
                </div>
            </div>

            <div className="mcp-list">
                {connections.length === 0 ? (
                    <div className="mcp-empty-state">
                        You haven&apos;t created any MCP connections yet.
                        <br />
                        Connect external agents (like Cursor) to your SecureBlob workspaces.
                    </div>
                ) : (
                    connections.map(conn => {
                        const knownWorkspaceIds = new Set(entries.map(entry => entry.blobId))
                        const missingWorkspaceIds = conn.workspaceBlobIds.filter(blobId => !knownWorkspaceIds.has(blobId))
                        const linkedOnlyRefs = (conn.linkedWorkspaceRefs ?? [])
                            .filter(ref => !conn.workspaceBlobIds.includes(ref) && !knownWorkspaceIds.has(ref))
                        const readerCount = Object.keys(conn.mcpReaderSeeds ?? {}).length
                        const writerCount = Object.keys(conn.mcpWriterSeeds ?? {}).length
                        return (
                        <div key={conn.configBlobId} className="mcp-list-item">
                            <div className="mcp-item-info">
                                <div className="mcp-item-name-row">
                                    <span className="mcp-item-name">{conn.label}</span>
                                    {conn.purpose && (
                                        <span className="mcp-ws-tag mcp-ws-tag-purpose">
                                            <GitBranch size={11} /> {conn.purpose}
                                        </span>
                                    )}
                                    <span className="mcp-ws-tag mcp-ws-tag-credential" title="Dedicated child credentials tracked in this registry entry">
                                        <KeyRound size={11} /> {readerCount}/{writerCount}
                                    </span>
                                    {requiresConnectionRecreation(conn) && (
                                        <span className="mcp-legacy-badge" title="This connection predates auth-v2 revoke semantics and should be recreated.">
                                            <AlertTriangle size={12} />
                                            Recreate
                                        </span>
                                    )}
                                </div>
                                <div className="mcp-item-meta">
                                    {(() => {
                                        const profile = engine.lookupKid(conn.ownerKid)
                                        const ownerLabel = profile
                                            ? `${profile.emoji ?? ''} ${profile.name ?? ''}`.trim() || conn.ownerKid.slice(0, 8) + '...'
                                            : conn.ownerKid.slice(0, 8) + '...'
                                        return <>Created by {ownerLabel} &middot; {new Date(conn.createdAt).toLocaleDateString()}</>
                                    })()}
                                </div>
	                                <div className="mcp-item-workspaces">
	                                    {conn.workspaceBlobIds.map(bid => {
	                                        const ws = entries.find(e => e.blobId === bid)
	                                        return ws ? (
	                                            <span key={bid} className="mcp-ws-tag">
	                                                <span>{ws.emoji}</span> {ws.label}
	                                            </span>
	                                        ) : (
	                                            <span key={bid} className="mcp-ws-tag mcp-ws-tag-missing">
	                                                <AlertTriangle size={11} /> {bid.slice(0, 12)}...
	                                            </span>
	                                        )
	                                    })}
                                        {linkedOnlyRefs.map(ref => (
                                            <span key={ref} className="mcp-ws-tag mcp-ws-tag-linked">
                                                <GitBranch size={11} /> {ref}
                                            </span>
                                        ))}
                                        {missingWorkspaceIds.length > 0 && (
                                            <span className="mcp-ws-tag mcp-ws-tag-missing">
                                                {missingWorkspaceIds.length} not in registry
                                            </span>
                                        )}
	                                </div>
	                            </div>
                            <div className="mcp-item-actions">
                                <button
                                    className="mcp-config-btn"
                                    onClick={() => setViewingConnection(conn)}
                                    title="Show setup instructions"
                                >
                                    <FileCode size={14} />
                                </button>
                                <button
                                    className="mcp-edit-btn"
                                    onClick={() => setEditingConnection(conn)}
                                    title={requiresConnectionRecreation(conn) ? 'This connection must be recreated instead of edited.' : 'Edit connection'}
                                    disabled={requiresConnectionRecreation(conn)}
                                >
                                    <Pencil size={14} />
                                </button>
                                <button
                                    className="mcp-revoke-btn"
                                    onClick={() => setPendingRevokeId(conn.configBlobId)}
                                    disabled={revokingId === conn.configBlobId}
                                >
                                    {revokingId === conn.configBlobId ? 'Revoking...' : 'Revoke'}
                                </button>
	                            </div>
	                        </div>
	                    )})
	                )}
            </div>

            {isCreateOpen && (
                <CreateMcpConnectionModal
                    isOpen={true}
                    onClose={() => setIsCreateOpen(false)}
                />
            )}

            {editingConnection && (
                <EditMcpConnectionModal
                    isOpen={true}
                    onClose={() => setEditingConnection(null)}
                    connection={editingConnection}
                />
            )}

            {viewingConnection && (
                <ViewMcpConfigModal
                    key={viewingConnection.configBlobId}
                    isOpen={true}
                    onClose={() => setViewingConnection(null)}
                    connection={viewingConnection}
                    onOpenConnection={(configBlobId) => {
                        const linked = connections.find(conn => conn.configBlobId === configBlobId)
                        if (!linked) {
                            toast(`Linked MCP config ${configBlobId} is not in this browser registry.`, { type: 'info' })
                            return false
                        }
                        setViewingConnection(linked)
                        return true
                    }}
                />
            )}

            {pendingRevokeId && createPortal(
                <div className="mcp-modal-overlay" onClick={() => setPendingRevokeId(null)}>
                    <div className="mcp-modal" onClick={e => e.stopPropagation()}>
                        <div className="mcp-modal-header">
                            <div className="mcp-modal-title">Revoke Connection</div>
                            <button className="mcp-modal-close" onClick={() => setPendingRevokeId(null)} aria-label="Close">
                                <X size={18} />
                            </button>
                        </div>
                        <div className="mcp-modal-body">
                            <p style={{ color: 'var(--color-text-primary)', fontSize: 14, lineHeight: 1.6 }}>
                                Are you sure you want to revoke this connection?
                                The access token will stop working.
                            </p>
                        </div>
                        <div className="mcp-modal-footer">
                            <button className="mcp-btn mcp-btn-secondary" onClick={() => setPendingRevokeId(null)}>Cancel</button>
                            <button className="mcp-btn mcp-btn-danger" onClick={handleRevokeConfirm}>Revoke Connection</button>
                        </div>
                    </div>
                </div>,
                document.body,
            )}

            <McpOAuthStateInspector
                isOpen={isOAuthInspectorOpen}
                onClose={() => setIsOAuthInspectorOpen(false)}
                connections={connections}
            />
        </div>
    )
}
